import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Save, Upload, AlertCircle } from 'lucide-react';
import { getToken } from '../lib/api';
import { Dialog } from '../components/Dialog';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

const MODES = [
  { v: 'off',            label: 'Off (manual review only)' },
  { v: 'mac_map',        label: 'MAC mapping (admin pre-maps each phone)' },
  { v: 'next_extension', label: 'Next free extension (auto-grab)' },
  { v: 'pin_enroll',     label: 'PIN enrollment from handset (*001 + 6-digit PIN)' },
];

export default function AutoProvisioning() {
  const qc = useQueryClient();
  const toast = useToast();
  const [domain, setDomain] = useState<string>('');
  const [showMap, setShowMap] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [mapForm, setMapForm] = useState<any>({});
  const [pinForm, setPinForm] = useState<any>({ ttl_hours: 24 });
  const [bulkCsv, setBulkCsv] = useState<string>('');

  const tenants = useQuery<any[]>({
    queryKey: ['tenants'],
    queryFn: () => fJson('/api/tenants'),
  });

  // default the picker to the first tenant
  const activeDomain = domain || tenants.data?.[0]?.domain_uuid || '';

  const rule = useQuery<any>({
    queryKey: ['provisioning-rule', activeDomain],
    queryFn: () => fJson(`/api/sbc/onboarding/rules/${activeDomain}`),
    enabled: !!activeDomain,
  });

  const macs = useQuery<any[]>({
    queryKey: ['mac-map', activeDomain],
    queryFn: () => fJson(`/api/sbc/onboarding/mac-map?domain_uuid=${activeDomain}`),
    enabled: !!activeDomain,
  });

  const pins = useQuery<any[]>({
    queryKey: ['enroll-pins'],
    queryFn: () => fJson('/api/sbc/onboarding/pin'),
    refetchInterval: 10000,
  });

  const audit = useQuery<any[]>({
    queryKey: ['discovery-audit', activeDomain],
    queryFn: () => fJson(`/api/sbc/onboarding/audit${activeDomain ? '?domain_uuid=' + activeDomain : ''}`),
    enabled: !!activeDomain,
    refetchInterval: 10000,
  });

  const saveRule = useMutation({
    mutationFn: (body: any) => fJson(`/api/sbc/onboarding/rules/${activeDomain}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['provisioning-rule', activeDomain] });
      toast('success', 'Rules saved');
    },
    onError: (e: any) => toast('error', e.message),
  });

  const addMap = useMutation({
    mutationFn: (body: any) => fJson('/api/sbc/onboarding/mac-map', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, domain_uuid: activeDomain }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mac-map', activeDomain] });
      setShowMap(false);
      setMapForm({});
    },
    onError: (e: any) => toast('error', e.message),
  });

  const removeMap = useMutation({
    mutationFn: (uuid: string) => fJson(`/api/sbc/onboarding/mac-map/${uuid}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mac-map', activeDomain] }),
  });

  const bulk = useMutation({
    mutationFn: () => fJson('/api/sbc/onboarding/mac-map/bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain_uuid: activeDomain, csv: bulkCsv }),
    }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['mac-map', activeDomain] });
      setShowBulk(false);
      setBulkCsv('');
      toast('success', `Imported ${r.imported}, skipped ${r.skipped}`);
    },
    onError: (e: any) => toast('error', e.message),
  });

  const issuePin = useMutation({
    mutationFn: () => fJson('/api/sbc/onboarding/pin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...pinForm, domain_uuid: activeDomain }),
    }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['enroll-pins'] });
      setShowPin(false);
      toast('success', `PIN ${r.pin_code} — user dials ${r.dial_string}`);
    },
    onError: (e: any) => toast('error', e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Auto-Provisioning</h1>
        <select className="input max-w-xs" value={activeDomain} onChange={(e) => setDomain(e.target.value)}>
          {(tenants.data || []).map((t: any) => (
            <option key={t.domain_uuid} value={t.domain_uuid}>
              {t.domain_description || t.domain_name}
            </option>
          ))}
        </select>
      </div>
      <p className="text-sm text-textSecondary">
        Per-tenant rules for what happens when an SBC discovers a new phone on the customer LAN. SBC tenant-binding scopes the rule — phones from a tenant-A SBC never assign to tenant-B.
      </p>

      {/* Rules card */}
      {rule.data && (
        <div className="card p-4 space-y-3">
          <div className="font-medium">Discovery rules</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-textSecondary mb-1">Mode</label>
              <select
                className="input"
                value={rule.data.mode}
                onChange={(e) => rule.data && qc.setQueryData(['provisioning-rule', activeDomain], { ...rule.data, mode: e.target.value })}
              >
                {MODES.map((m) => (
                  <option key={m.v} value={m.v}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-3">
              <label className="text-sm flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!rule.data.auto_create_extension}
                  onChange={(e) => qc.setQueryData(['provisioning-rule', activeDomain], { ...rule.data, auto_create_extension: e.target.checked })}
                /> Auto-create extension if missing
              </label>
              <label className="text-sm flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!rule.data.auto_reboot}
                  onChange={(e) => qc.setQueryData(['provisioning-rule', activeDomain], { ...rule.data, auto_reboot: e.target.checked })}
                /> Auto-reboot phone
              </label>
              <label className="text-sm flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!rule.data.require_admin_approval}
                  onChange={(e) => qc.setQueryData(['provisioning-rule', activeDomain], { ...rule.data, require_admin_approval: e.target.checked })}
                /> Require admin approval
              </label>
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">Extension floor</label>
              <input
                className="input"
                type="number"
                value={rule.data.extension_floor || 1000}
                onChange={(e) => qc.setQueryData(['provisioning-rule', activeDomain], { ...rule.data, extension_floor: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">Extension ceiling</label>
              <input
                className="input"
                type="number"
                value={rule.data.extension_ceiling || 1999}
                onChange={(e) => qc.setQueryData(['provisioning-rule', activeDomain], { ...rule.data, extension_ceiling: parseInt(e.target.value) })}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm text-textSecondary mb-1">Default device label template</label>
              <input
                className="input font-mono"
                value={rule.data.default_label_template || ''}
                onChange={(e) => qc.setQueryData(['provisioning-rule', activeDomain], { ...rule.data, default_label_template: e.target.value })}
                placeholder="{{model}} ({{mac_short}})"
              />
              <div className="text-xs text-textSecondary mt-1">
                Variables: <code>{'{{model}}'}</code> <code>{'{{vendor}}'}</code> <code>{'{{mac_short}}'}</code> <code>{'{{mac}}'}</code> <code>{'{{extension}}'}</code>
              </div>
            </div>
          </div>
          <button
            onClick={() => saveRule.mutate(rule.data)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Save size={14} /> Save rules
          </button>
        </div>
      )}

      {/* MAC mappings */}
      <div className="card p-0 overflow-hidden">
        <div className="p-3 flex items-center justify-between border-b border-border">
          <div className="font-medium">MAC pre-mappings</div>
          <div className="flex gap-2">
            <button onClick={() => setShowBulk(true)} className="btn-ghost inline-flex items-center gap-2">
              <Upload size={14} /> Bulk import (CSV)
            </button>
            <button onClick={() => { setMapForm({}); setShowMap(true); }} className="btn-primary inline-flex items-center gap-2">
              <Plus size={14} /> Add mapping
            </button>
          </div>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>MAC</th>
              <th>Extension</th>
              <th>Label</th>
              <th>Status</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(macs.data || []).map((m: any) => (
              <tr key={m.mapping_uuid} className="hover:bg-elevated">
                <td className="font-mono">{m.mac_address}</td>
                <td>{m.extension}</td>
                <td>{m.label || <span className="text-textSecondary">—</span>}</td>
                <td className="text-xs">{m.fulfilled_at ? <span className="text-success">fulfilled</span> : <span className="text-textSecondary">awaiting phone</span>}</td>
                <td className="text-xs">{new Date(m.created_at).toLocaleString()}</td>
                <td>
                  <button onClick={() => removeMap.mutate(m.mapping_uuid)} className="btn-ghost p-1 text-danger"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!macs.data?.length && <div className="p-6 text-center text-textSecondary">No MAC mappings for this tenant.</div>}
      </div>

      {/* Active enrollment PINs */}
      <div className="card p-0 overflow-hidden">
        <div className="p-3 flex items-center justify-between border-b border-border">
          <div className="font-medium">Active enrollment PINs</div>
          <button onClick={() => { setPinForm({ ttl_hours: 24 }); setShowPin(true); }} className="btn-primary inline-flex items-center gap-2">
            <Plus size={14} /> Issue PIN
          </button>
        </div>
        <table className="table">
          <thead>
            <tr><th>Extension</th><th>PIN</th><th>Dial string</th><th>Expires</th><th>Issued</th></tr>
          </thead>
          <tbody>
            {(pins.data || []).filter((p: any) => p.domain_uuid === activeDomain).map((p: any) => (
              <tr key={p.pin_uuid} className="hover:bg-elevated">
                <td>{p.extension}</td>
                <td className="font-mono">{p.pin_code}</td>
                <td className="font-mono">*001{p.pin_code}</td>
                <td className="text-xs">{new Date(p.expires_at).toLocaleString()}</td>
                <td className="text-xs">{new Date(p.issued_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!pins.data?.filter((p: any) => p.domain_uuid === activeDomain).length && <div className="p-6 text-center text-textSecondary">No active PINs. Issue one for an extension and tell the user to dial <code className="bg-elevated px-1 font-mono">*001PIN</code> on the handset.</div>}
      </div>

      {/* Audit feed */}
      <div className="card p-0 overflow-hidden">
        <div className="p-3 font-medium border-b border-border flex items-center gap-2">
          <AlertCircle size={14} /> Discovery audit
        </div>
        <table className="table">
          <thead><tr><th>When</th><th>Action</th><th>Vendor</th><th>MAC</th><th>IP</th><th>Extension</th><th>Note</th></tr></thead>
          <tbody>
            {(audit.data || []).slice(0, 50).map((a: any) => (
              <tr key={a.audit_id} className="hover:bg-elevated">
                <td className="text-xs">{new Date(a.occurred_at).toLocaleString()}</td>
                <td><span className={
                  a.action === 'auto_assigned' ? 'text-success' :
                  a.action === 'mac_map_hit' ? 'text-success' :
                  a.action === 'pin_enrolled' ? 'text-success' :
                  a.action === 'rejected' ? 'text-danger' :
                  'text-textSecondary'
                }>{a.action}</span></td>
                <td className="text-xs">{a.vendor_key || '—'}</td>
                <td className="font-mono text-xs">{a.mac_address || '—'}</td>
                <td className="font-mono text-xs">{a.ip_address || '—'}</td>
                <td>{a.extension || '—'}</td>
                <td className="text-xs text-textSecondary">{a.note || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!audit.data?.length && <div className="p-6 text-center text-textSecondary">No discovery activity yet.</div>}
      </div>

      {/* Add MAC dialog */}
      <Dialog
        open={showMap}
        onClose={() => setShowMap(false)}
        title="Add MAC pre-mapping"
        size="md"
        footer={
          <>
            <button onClick={() => setShowMap(false)} className="btn-ghost">Cancel</button>
            <button onClick={() => addMap.mutate(mapForm)} className="btn-primary">Add</button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-textSecondary mb-1">MAC address *</label>
            <input className="input font-mono" placeholder="AA:BB:CC:DD:EE:FF" value={mapForm.mac_address || ''} onChange={(e) => setMapForm({ ...mapForm, mac_address: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Extension *</label>
            <input className="input" placeholder="1001" value={mapForm.extension || ''} onChange={(e) => setMapForm({ ...mapForm, extension: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Label</label>
            <input className="input" placeholder="Front desk Yealink" value={mapForm.label || ''} onChange={(e) => setMapForm({ ...mapForm, label: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Notes</label>
            <input className="input" value={mapForm.notes || ''} onChange={(e) => setMapForm({ ...mapForm, notes: e.target.value })} />
          </div>
        </div>
      </Dialog>

      {/* Bulk CSV dialog */}
      <Dialog
        open={showBulk}
        onClose={() => setShowBulk(false)}
        title="Bulk import MAC mappings"
        size="lg"
        footer={
          <>
            <button onClick={() => setShowBulk(false)} className="btn-ghost">Cancel</button>
            <button onClick={() => bulk.mutate()} className="btn-primary">Import</button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-textSecondary">CSV columns: <code>mac,extension,label,notes</code> — one per line, no header.</p>
          <textarea
            className="input font-mono text-xs"
            rows={10}
            value={bulkCsv}
            onChange={(e) => setBulkCsv(e.target.value)}
            placeholder="00:0b:82:11:22:33,1001,Front desk,Acme HQ&#10;c0:74:ad:aa:bb:cc,1002,Bob's office,&#10;..."
          />
        </div>
      </Dialog>

      {/* Issue PIN dialog */}
      <Dialog
        open={showPin}
        onClose={() => setShowPin(false)}
        title="Issue enrollment PIN"
        size="md"
        footer={
          <>
            <button onClick={() => setShowPin(false)} className="btn-ghost">Cancel</button>
            <button onClick={() => issuePin.mutate()} className="btn-primary">Issue</button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-textSecondary">User dials <code className="bg-elevated px-1 font-mono">*001PIN</code> on the handset. The phone's MAC is captured from the SBC's ARP table and bound to this extension.</p>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Extension *</label>
            <input className="input" placeholder="1001" value={pinForm.extension || ''} onChange={(e) => setPinForm({ ...pinForm, extension: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Valid for (hours)</label>
            <input className="input" type="number" min={1} max={168} value={pinForm.ttl_hours || 24} onChange={(e) => setPinForm({ ...pinForm, ttl_hours: parseInt(e.target.value) || 24 })} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
